import { RequestStatus } from 'features/request/requestSlice';
import type { ConstructionTypeData } from 'gql/graphql';
import { WorkInSumEnum } from 'gql/graphql';
import { useCatalogWorkTypes } from 'pages/Project/UerProject/WorkOrder/hooks/useCatalogWorkTypes';
import { Loader, Informer, Grid, Checkbox, Text, IconQuestion, IconWarning } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import { GridItem } from '@consta/uikit/Grid';
import { TextField } from '@consta/uikit/TextField';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { workInSumEnumItems } from '../../constantsProject';
import { ComponentWithTooltip } from '../ComponentWithTooltip';
import { FilesSelector } from '../FilesSelector/FilesSelector';
import type { ITabItem } from '../Form';
import { ControlledSelectField, ControlledTextField, ControlNumberField, ControlledTabs } from '../Form';
import { ControlledSearchableSelectField } from '../Form/ControlledSearchableSelectField';

import { ControlUerRatioSelector } from './EditUerRatioSelector';
import { useTabStyles } from './hooks/useTabStyles';

import Styles from './EditUerForm.module.css';

interface IEditUerForm {
    isDataFetching: boolean;
    uerId?: string | number;
    isUnableToLoad: boolean | undefined;
    isFileSelectorVisible: boolean;
    isFileSelectorLoading: boolean;
    onFileSelectorFilesChange: (files?: File[]) => void;
    activeTab: ITabItem;
    onTabChange: ({ value }: any) => void;
    tabItems: ITabItem[];
    control: Control;
    fieldsToCheck: Record<number, string[]>;
    uerClassifierItems: { id: string; label: string }[];
    isUerClassifierFieldDisabled: boolean;
    uerChaptersItems: { id: string; label: string }[];
    isChapterFieldDisabled: boolean;
    areControlsDisabled: boolean;
    contractConstructionTypes: ConstructionTypeData[];
    onConstructionTypeCheckChange: (id: number) => ({ checked }: { checked: boolean }) => void;
    checkIsConstructionTypeCheckboxCheckedHandler: (id: number) => boolean;
    checkIsConstructionTypeCheckboxDisabledHandler: (id: number) => boolean;
    checkIsConstructionTypeEditableHandler: (id: number) => boolean;
    uerCode: string;
    ratioGroupIdsControl: { control: Control; name: string };
    ratioIdsControl: { control: Control; name: string };
    dismantlingCoefControl: { control: Control; name: string };
    ratioItemsUsedIds: any[];
    selectedFile?: any;
    hasPriceListRatio?: boolean;
    setValue: UseFormSetValue<Record<string, any>>;
    hasWorkKind: boolean;
}

const WorkKindQuestionWithTooltip = () => (
    <ComponentWithTooltip
        tooltipText="В случае, если необходимый вид работ отсутствует, его можно создать в справочнике видов работ"
        styles={{ height: 16, marginLeft: 10 }}
        direction="upCenter"
    >
        <IconQuestion view="secondary" size="s" />
    </ComponentWithTooltip>
);

WorkKindQuestionWithTooltip.displayName = 'WorkKindQuestionWithTooltip';

const WorkRatioQuestionWithTooltip = () => (
    <ComponentWithTooltip
        tooltipText="К перехода доступен для ввода/редактирования на форме НЗ/Изм. к НЗ"
        styles={{ height: 16, marginLeft: 10 }}
        direction="upCenter"
    >
        <IconQuestion view="secondary" size="s" />
    </ComponentWithTooltip>
);

WorkRatioQuestionWithTooltip.displayName = 'WorkRatioQuestionWithTooltip';

const workRatioRules = {
    validate: (value: any, formData: { workInSum: { id: WorkInSumEnum } }) => {
        if (!value) {
            if (![WorkInSumEnum.ParticipateWithRatio, WorkInSumEnum.Participate].includes(formData?.workInSum?.id)) {
                return true;
            }

            return 'Поле обязательно для заполнения';
        }

        return true;
    },
};

export const EditUerForm: React.FC<IEditUerForm> = (props) => {
    const {
        isDataFetching,
        uerId,
        isUnableToLoad,
        isFileSelectorVisible,
        onFileSelectorFilesChange,
        isFileSelectorLoading,
        activeTab,
        onTabChange,
        tabItems,
        control,
        fieldsToCheck,
        uerClassifierItems,
        isUerClassifierFieldDisabled,
        uerChaptersItems,
        isChapterFieldDisabled,
        areControlsDisabled,
        contractConstructionTypes,
        onConstructionTypeCheckChange,
        checkIsConstructionTypeCheckboxCheckedHandler,
        checkIsConstructionTypeCheckboxDisabledHandler,
        checkIsConstructionTypeEditableHandler,
        uerCode,
        ratioGroupIdsControl,
        ratioIdsControl,
        dismantlingCoefControl,
        ratioItemsUsedIds,
        selectedFile,
        hasPriceListRatio = true,
        setValue,
        hasWorkKind,
    } = props;

    const { catalogWorkTypes, catalogWorkTypesRequest, onNextPageRequest, hasNextPage, catalogWorkTypesStatus } =
        useCatalogWorkTypes();

    const workKindItems = useMemo(
        () =>
            catalogWorkTypes.map((item) => ({
                id: item.uniqueId,
                label: item.name,
                workCode: item.code,
                workUnit: item.unit,
            })),
        [catalogWorkTypes],
    );

    const currentWorkKindItem = useWatch({ control, name: 'workKind' });
    const currentWorkInSum = useWatch({ control, name: 'workInSum' });

    const tabStyles = useTabStyles({ tabItems, activeTab });

    useEffect(() => {
        if (currentWorkKindItem) {
            setValue('workCode', currentWorkKindItem.workCode);
            setValue('workUnit', currentWorkKindItem.workUnit);
        }
    }, [currentWorkKindItem, setValue]);

    const isWorkRatioEnabled = useMemo(
        () => [WorkInSumEnum.ParticipateWithRatio, WorkInSumEnum.Participate].includes(currentWorkInSum?.id),
        [currentWorkInSum?.id],
    );

    useEffect(() => {
        if (!isWorkRatioEnabled) {
            setValue('workRatio', null);
        }
    }, [isWorkRatioEnabled, setValue]);

    return isDataFetching ? (
        <div className={Styles.modalBody}>
            <Loader />
        </div>
    ) : (
        <div className={Styles.modalBody}>
            {!isUnableToLoad && (
                <>
                    {!uerId && (
                        <Informer
                            size="s"
                            status="warning"
                            icon={IconWarning}
                            label="Новая расценка будет добавлена в Прейскурант расценок. Применение коэффициентов к расценке обновит Прейскурант коэффициентов"
                            className={Styles.informer}
                        />
                    )}
                    {isFileSelectorVisible && (
                        <div
                            className={`${Styles.heightFilesSelector} ${
                                selectedFile?.length ? Styles.selectedFile : ''
                            }`}
                        >
                            <FilesSelector
                                accept={['.xml']}
                                isLoading={isFileSelectorLoading}
                                typeName=".xml"
                                manualOperationName="выберите файл вручную"
                                onFilesChange={onFileSelectorFilesChange}
                                isExternalFilesState
                                files={selectedFile}
                            />
                        </div>
                    )}
                </>
            )}
            <ControlledTabs
                value={activeTab}
                onChange={onTabChange}
                items={tabItems}
                view="bordered"
                size="s"
                control={control}
                fieldsToCheck={fieldsToCheck}
                transparentTag
                style={{ marginTop: '14px' }}
            />
            <div className={Styles.tabsBody}>
                <Grid rowGap="s" cols="1" style={tabStyles[0]}>
                    <GridItem>
                        <ControlledTextField
                            name="code"
                            label="Шифр"
                            control={control}
                            type="text"
                            disabled
                            rules={requiredBaseRule}
                            isTooltipHidden={!!tabStyles[0]}
                            placeholder=""
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="name"
                            label="Наименование"
                            control={control}
                            type="textarea"
                            disabled
                            rules={requiredBaseRule}
                            isTooltipHidden={!!tabStyles[0]}
                            placeholder=""
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="unit"
                            label="Ед. изм."
                            control={control}
                            type="text"
                            disabled
                            rules={requiredBaseRule}
                            isTooltipHidden={!!tabStyles[0]}
                            placeholder=""
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="basePrice.unitCost"
                            label={
                                (
                                    <span>
                                        Стоимость единицы УЕР<div>в базисном уровне цен 2001 года, руб.без НДС</div>
                                    </span>
                                ) as any
                            }
                            labelPosition="left"
                            control={control}
                            disabled
                            size="s"
                            rules={requiredBaseRule}
                            className={Styles.basePriceField}
                            isTooltipHidden={!!tabStyles[0]}
                            placeholder=""
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="uerClassifier"
                            label="Тип УЕР"
                            control={control}
                            items={uerClassifierItems}
                            rules={requiredBaseRule}
                            disabled={isUerClassifierFieldDisabled}
                            isTooltipHidden={!!tabStyles[0]}
                            placeholder={isUerClassifierFieldDisabled ? '' : 'Выберите'}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="chapter"
                            label="Глава в расчете"
                            control={control}
                            items={uerChaptersItems}
                            rules={requiredBaseRule}
                            disabled={isChapterFieldDisabled}
                            isTooltipHidden={!!tabStyles[0]}
                            placeholder=""
                        />
                    </GridItem>
                    <GridItem>
                        <ControlUerRatioSelector
                            label="Коэффициенты к расценке"
                            code={uerCode}
                            ratioGroupIdsControl={ratioGroupIdsControl}
                            ratioIdsControl={ratioIdsControl}
                            dismantlingCoefControl={dismantlingCoefControl}
                            disabled={isUnableToLoad || areControlsDisabled || !hasPriceListRatio}
                            disabledIds={ratioItemsUsedIds}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="justification"
                            label="Обоснование"
                            control={control}
                            type="textarea"
                            rules={!isUnableToLoad ? requiredBaseRule : undefined}
                            disabled={isUnableToLoad || areControlsDisabled}
                            isTooltipHidden={!!tabStyles[0]}
                            placeholder={isUnableToLoad || areControlsDisabled ? '' : 'Введите'}
                        />
                    </GridItem>
                </Grid>
                <Grid rowGap="s" cols="1" style={tabStyles[1]}>
                    <div className={Styles.wrapperTextInfo}>
                        <Text size="s" view="secondary">
                            Вид строительства
                        </Text>
                        <Text className={Styles.columnsTextField} size="s" view="secondary">
                            Стоимость единицы в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без
                            НДС
                        </Text>
                    </div>
                    {contractConstructionTypes?.map(({ id, name }: any) => (
                        <GridItem key={id}>
                            <div className={Styles.checkboxRow}>
                                <Checkbox
                                    id={String(id)}
                                    className={Styles.checkbox}
                                    checked={checkIsConstructionTypeCheckboxCheckedHandler(id)}
                                    onChange={onConstructionTypeCheckChange(id)}
                                    style={{ marginTop: 4 }}
                                    disabled={checkIsConstructionTypeCheckboxDisabledHandler(id)}
                                />
                                {checkIsConstructionTypeEditableHandler(id) ? (
                                    <ControlNumberField
                                        name={`${id}.unitCostWithOther`}
                                        label={name}
                                        control={control}
                                        type="text"
                                        disabled
                                        size="s"
                                        labelPosition="left"
                                        className={Styles.unitCostField}
                                    />
                                ) : (
                                    <TextField
                                        disabled
                                        size="s"
                                        labelPosition="left"
                                        className={Styles.unitCostField}
                                        label={name}
                                    />
                                )}
                            </div>
                        </GridItem>
                    ))}
                </Grid>
                {hasWorkKind && (
                    <Grid rowGap="s" cols="1" style={tabStyles[2]}>
                        <GridItem>
                            <ControlledSearchableSelectField
                                name="workKind"
                                label="Вид работ"
                                control={control}
                                items={workKindItems}
                                labelIcon={WorkKindQuestionWithTooltip}
                                className="labelWithEndingIcon"
                                rules={requiredBaseRule}
                                labelPosition="left"
                                isTooltipHidden={!!tabStyles[2]}
                                direction="rightCenter"
                                onSearch={catalogWorkTypesRequest}
                                isLazyLoadEnabled
                                onNextPageRequest={onNextPageRequest}
                                hasNextPage={hasNextPage}
                                isLoading={catalogWorkTypesStatus === RequestStatus.FETCHING}
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                placeholder=""
                                name="workCode"
                                label="Шифр"
                                control={control}
                                type="text"
                                disabled
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                placeholder=""
                                name="workUnit"
                                label="Ед. изм."
                                control={control}
                                type="text"
                                disabled
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledSelectField
                                name="workInSum"
                                label="Участие в суммировании &#8232;ФО по виду работ"
                                control={control}
                                items={workInSumEnumItems}
                                rules={requiredBaseRule}
                                isTooltipHidden={!!tabStyles[2]}
                            />
                        </GridItem>
                        <GridItem>
                            <ControlNumberField
                                name="workRatio"
                                label="К перехода"
                                control={control}
                                type="text"
                                size="s"
                                decimalScale={4}
                                disabled={!isWorkRatioEnabled}
                                placeholder="Введите"
                                labelIcon={WorkRatioQuestionWithTooltip}
                                labelPosition="left"
                                className="labelWithEndingIcon"
                                width="full"
                                rules={workRatioRules as any}
                                isTooltipHidden={!!tabStyles[2]}
                            />
                        </GridItem>
                    </Grid>
                )}
            </div>
        </div>
    );
};

EditUerForm.displayName = 'EditUerForm';
